import { RefObject, useEffect, useState } from 'react';
import { hashSHA256 } from '@/libs/hash';
import { constrainFloatingPosition } from './draggable-position';

export const useDisableBodyScroll = (open: boolean) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // khôi phục cuộn nếu component bị unmount khi menu đang mở (vd: chuyển trang)
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);
};

export const useHashedFingerprint = () => {
  const [hashedFingerprint, setHashedFingerprint] = useState<string | null>(
    null
  );

  useEffect(() => {
    const getHashedFingerprint = async () => {
      // tải thư viện fingerprint khi cần (không nhét vào bundle chung của mọi trang)
      const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const hashed = await hashSHA256(result.visitorId);
      setHashedFingerprint(hashed);
    };

    getHashedFingerprint();
  }, []);

  return hashedFingerprint;
};

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}

/**
 * Giữ phần tử `position: fixed; bottom: 0` sát đáy vùng đang nhìn thấy.
 * Safari iOS (thanh địa chỉ nổi): khi cuộn lên, thanh địa chỉ đổi kích thước nhưng phần tử fixed
 * bottom:0 không bám theo → hở một khoảng lộ nội dung trang. Hook đo visualViewport và dịch phần tử
 * XUỐNG cho sát đáy (không đẩy lên khi mở bàn phím). Trình duyệt khác: không làm gì.
 */
export const useStickToVisualBottom = (ref: RefObject<HTMLElement>, enabled: boolean = true) => {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const el = ref.current;
    if (!enabled || !vv || !el) return;
    let raf = 0;
    let shift = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const baseBottom = el.getBoundingClientRect().bottom - shift;
        const visibleBottom = vv.offsetTop + vv.height;
        const next = visibleBottom - baseBottom > 1 ? Math.round(visibleBottom - baseBottom) : 0;
        if (next !== shift) {
          shift = next;
          el.style.transform = shift ? `translateY(${shift}px)` : '';
        }
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref, enabled]);
};

/**
 * Cho phần tử nổi (fixed) kéo thả được bằng tay/chuột. Chạm nhẹ vẫn bấm như cũ,
 * kéo > 6px mới tính là kéo (và chặn click lần đó). Vị trí được nhớ theo storageKey.
 */
export const useDraggable = (
  ref: RefObject<HTMLElement>,
  { enabled = true, storageKey, bottomGap = 0, snapToEdge = false }: { enabled?: boolean; storageKey?: string; bottomGap?: number; snapToEdge?: boolean } = {}
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    let dx = 0, dy = 0, startX = 0, startY = 0, baseX = 0, baseY = 0;
    let active = false, moved = false;
    try {
      const saved = storageKey && JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved) { dx = +saved.x || 0; dy = +saved.y || 0; }
    } catch { /* bỏ qua */ }
    const apply = () => { el.style.transform = dx || dy ? `translate(${dx}px, ${dy}px)` : ''; };
    const clamp = (snap = false) => {
      const prev = el.style.transform;
      el.style.transform = '';
      const r = el.getBoundingClientRect();
      el.style.transform = prev;
      const vw = window.innerWidth;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const position = constrainFloatingPosition(
        { x: r.left + dx, y: r.top + dy },
        { width: r.width, height: r.height },
        { width: vw, height: vh, bottomGap },
        snap
      );
      dx = position.x - r.left;
      dy = position.y - r.top;
      apply();
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      active = true; moved = false;
      startX = e.clientX; startY = e.clientY; baseX = dx; baseY = dy;
    };
    const onMove = (e: PointerEvent) => {
      if (!active) return;
      const mx = e.clientX - startX, my = e.clientY - startY;
      if (!moved && Math.hypot(mx, my) < 6) return;
      moved = true;
      e.preventDefault();
      dx = baseX + mx; dy = baseY + my;
      clamp();
    };
    const onUp = () => {
      if (!active) return;
      active = false;
      if (!moved) return;
      clamp(snapToEdge);
      try { storageKey && localStorage.setItem(storageKey, JSON.stringify({ x: dx, y: dy })); } catch { /* bỏ qua */ }
      // vừa kéo xong thì không tính là bấm
      const stop = (ev: Event) => { ev.preventDefault(); ev.stopPropagation(); };
      el.addEventListener('click', stop, { capture: true, once: true });
      setTimeout(() => el.removeEventListener('click', stop, { capture: true } as any), 300);
    };
    const noNativeDrag = (e: Event) => e.preventDefault();
    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('dragstart', noNativeDrag);
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const onResize = () => clamp(snapToEdge);
    window.addEventListener('resize', onResize);
    clamp();
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('dragstart', noNativeDrag);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('resize', onResize);
      el.style.touchAction = '';
      el.style.transform = '';
    };
  }, [ref, enabled, storageKey, bottomGap, snapToEdge]);
};
